import { useEffect, useRef, useReducer } from 'react';

import { eventManager, Event } from '../core';

import {
  Id,
  ToastItemProps,
  ToastContent,
  ToastItem,
} from '../components/ToastItem/ToastItem.types';

import { ToastProps } from '../Toast.types';

export type ContainerInstance = {
  toastKey: number;
  props: ToastProps;
};

export function useToastContainer(props: ToastProps) {
  const [, forceUpdate] = useReducer(x => x + 1, 0);
  const containerRef = useRef(null);
  const toastToRender = useRef(new Map<Id, ToastItem>()).current;
  const instance = useRef<ContainerInstance>({
    toastKey: 1,
    props,
  }).current;

  useEffect(() => {
    eventManager
      .cancelEmit(Event.WillUnmount)
      .on(Event.Show, buildToast)
      .on(Event.Clear, toastId => containerRef.current && removeToast(toastId))
      .emit(Event.DidMount, instance);

    return () => {
      toastToRender.clear();
      eventManager.emit(Event.WillUnmount, instance);
    };
    // TODO: fix ESLint error
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    instance.props = props;
  });

  const removeToast = (toastId?: Id) => {
    if (toastId !== undefined && toastToRender.has(toastId)) {
      toastToRender.delete(toastId);

      forceUpdate();
    }
  };

  const buildToast = (content: ToastContent, options: ToastItemProps) => {
    const toastProps: ToastItemProps = {
      ...options,
      key: instance.toastKey++,
      onClose: event => {
        event?.preventDefault();
        event?.stopPropagation();
        removeToast(options.toastId);
        options.onClose?.(event);
      },
    };

    appendToast(content, toastProps);
  };

  const appendToast = (content: ToastContent, toastProps: ToastItemProps) => {
    if (!containerRef.current) return;

    const { toastId } = toastProps;

    const toast = {
      content,
      props: toastProps,
    };
    toastToRender.set(toastId, toast);
    forceUpdate();
  };

  return {
    toasts: Array.from(toastToRender.values()),
    containerRef,
  };
}
